import type { ClientChat } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text } from 'react-native';
import { Badge } from 'react-native-paper';

import { useChatPartner } from '@/features/chats/hooks/useChatPartner';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import { AppRoute } from '@/navigation/types/navigation';
import { PastTime } from '@/shared/components/PastTime';
import { getPersonName } from '@/shared/utils/person';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { dayjs } from '@/time';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    item: {
      paddingVertical: 2,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    pretext: { fontWeight: 'bold' },
    blocked: { fontStyle: 'italic' },
    deleted: { fontStyle: 'italic' }
  });

type Props = {
  chat: ClientChat;
};

export const ChatListItem: FC<Props> = ({ chat }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'components.chatListItem'
  });
  const chatPartner = useChatPartner(chat);

  if (!chatPartner) {
    return <></>;
  }

  const deleted = (chatPartner.deleted || chat.lastMessage?.deleted) ?? false;
  const blocked = chatPartner?.filterStatus === 'BLOCKED';
  const personName = chatPartner.deleted
    ? t('accountDeleted')
    : getPersonName(chatPartner);

  const lastMessage = chat.lastMessage;
  const lastMessageTime = dayjs(lastMessage?.created);
  const unreadMessageCount = chat.unreadMessageCount ?? 0;

  return (
    <NavigationListItem
      screen={AppRoute.Chat}
      params={{ id: chat.id, personName }}
      icon={() => <PersonAvatar person={chatPartner} />}
      style={styles.item}
      title={personName}
      description={
        blocked ? (
          <Text style={styles.blocked}>{t('personBlocked')}</Text>
        ) : (
          <>
            <Text style={styles.pretext}>
              <PastTime time={lastMessageTime} />
              {': '}
            </Text>
            {deleted ? (
              <Text style={styles.deleted}>{t('messageDeleted')}</Text>
            ) : (
              <Text>{lastMessage?.message}</Text>
            )}
          </>
        )
      }
      textOrder="column-reverse"
      badge={
        unreadMessageCount > 0 && (
          <Badge theme={{ colors: { error: theme.colors.error } }}>
            {chat.unreadMessageCount}
          </Badge>
        )
      }
    />
  );
};
